import { HttpErrorResponse } from "@angular/common/http";
import { toDateString } from "../helpers";

export interface HierarchyTreeItem {
    organizationId: number;
    organizationName: string;
    isCurrent?: boolean;
    containCurrent?: boolean;
    status: HierarchyTreeItemStatus;

    logex?: OrganizationLocationRelation;
    mrdm?: OrganizationLocationRelation;
    official?: OrganizationLocationRelation;

    children: HierarchyTreeItem[];
}

export enum HierarchyTreeItemStatus {
    Old = "old",
    Active = "active",
    Planned = "planned"
}

export interface OrganizationRelationsData {
    groups: OrganizationLocationData[];
    topLevelGroupIds: number[];
}

export class OrganizationRelations {
    groups: OrganizationLocation[];
    topLevelGroupIds: number[];

    constructor(data?: OrganizationRelationsData) {
        if (data) {
            this.groups = data.groups ? data.groups.map(x => new OrganizationLocation(x)) : [];
            this.topLevelGroupIds = data.topLevelGroupIds || [];
        }
    }
}

export interface OrganizationRelation {
    parentId: number;
    locations: OrganizationLocation[];
}

export interface OrganizationLocationData {
    hiearchyGroupId: number;
    toOrganizationId: number;
    fromOrganizationId: number;
    organizationHierarchyTypeCode: string;
    logex: OrganizationLocationRelationData;
    mrdm: OrganizationLocationRelationData;
    official: OrganizationLocationRelationData;
    isDeleted: boolean;
}

export function OrganizationLocationToData(
    location: OrganizationLocation
): OrganizationLocationData {
    return {
        hiearchyGroupId: location.hiearchyGroupId,
        toOrganizationId: location.toOrganizationId,
        fromOrganizationId: location.fromOrganizationId,
        organizationHierarchyTypeCode: location.organizationHierarchyTypeCode,
        logex: {
            id: location.logex.id,
            activeFrom: toDateString(location.logex.activeFrom),
            activeTo: toDateString(location.logex.activeTo),
            legallyBound: location.logex.legallyBound
        },
        mrdm: {
            id: location.mrdm.id,
            activeFrom: toDateString(location.mrdm.activeFrom),
            activeTo: toDateString(location.mrdm.activeTo),
            legallyBound: location.mrdm.legallyBound
        },
        official: {
            id: location.official.id,
            activeFrom: toDateString(location.official.activeFrom),
            activeTo: toDateString(location.official.activeTo),
            legallyBound: location.official.legallyBound
        },
        isDeleted: location.isDeleted
    };
}

export class OrganizationLocation {
    hiearchyGroupId?: number;
    toOrganizationId?: number;
    fromOrganizationId: number;
    organizationHierarchyTypeCode: string;
    logex: OrganizationLocationRelation;
    mrdm: OrganizationLocationRelation;
    official: OrganizationLocationRelation;
    isDeleted: boolean;

    constructor(data?: OrganizationLocationData) {
        if (data) {
            this.hiearchyGroupId = data.hiearchyGroupId;
            this.toOrganizationId = data.toOrganizationId;
            this.fromOrganizationId = data.fromOrganizationId;
            this.organizationHierarchyTypeCode = data.organizationHierarchyTypeCode;
            this.logex = new OrganizationLocationRelation(data.logex);
            this.mrdm = new OrganizationLocationRelation(data.mrdm);
            this.official = new OrganizationLocationRelation(data.official);
            this.isDeleted = data.isDeleted;
        }
    }
}

export interface OrganizationLocationRelationData {
    id?: number;
    activeFrom?: string;
    activeTo?: string;
    legallyBound: boolean;
}

export class OrganizationLocationRelation {
    id?: number;
    activeFrom?: Date;
    activeTo?: Date;
    legallyBound: boolean;

    constructor(data?: OrganizationLocationRelationData) {
        if (data) {
            this.id = data.id;
            this.activeFrom = data.activeFrom ? new Date(data.activeFrom) : null;
            this.activeTo = data.activeTo ? new Date(data.activeTo) : null;
            this.legallyBound = data.legallyBound;
        }
    }
}

export enum OrganizationHierarchyType {
    Location = "OrganizationHierarchyTypeCode.Location",
    Msb = "OrganizationHierarchyTypeCode.Msb",
    Split = "OrganizationHierarchyTypeCode.Split",
    Merger = "OrganizationHierarchyTypeCode.Merger"
}

export interface RelationSaveError extends HttpErrorResponse {
    error: {
        ErrorId: string;
        ErrorCode: string;
        ErrorData: string;
    };
}

export interface RelationSaveErrorData {
    ToOrganizationId: number;
    FromOrganizationId: number;
}

export interface SaveConfirmWarning {
    codeLc:
        | ".SaveConfirm.AddLegalFor"
        | ".SaveConfirm.RemoveLegalFor"
        | ".SaveConfirm.LocationNotActivated"
        | ".SaveConfirm.DeleteLocation";
    locationId: number;
    parentId: number;
    company?: "Logex" | "MRDM";
}
